/**
 * **The profile switcher** (R4, A7; Thomas, 2026-10-08: D81, D92). In the banner, where the name of the open
 * profile always was: the person's own profile first, then each company they belong to, by name — the order the
 * server lists them in. Choosing one opens it on its own landing page.
 *
 * **Each says in words what it is.** The person's own profile is *Personal*, not the address it happens to be named
 * after, and says that it is their own; a company is its name as Brønnøysund gives it, and says it is an
 * organisation, with its number where the Org has one. A glyph beside each helps the eye and decides nothing.
 *
 * It replaced the *Switch* list on Settings, which was a select reached through a menu entry: the one control a
 * person with two profiles uses most was two clicks from every screen and on none of them.
 *
 * **The only switcher, and it lists profiles only** (F356; Thomas, 2026-10-11). A second one, top-left, chose a
 * company inside the open profile, and named that company while this one named the profile — two switchers that
 * could disagree. That one is gone and this one sits in its place: a company held inside a profile is not a profile,
 * so it is never listed here; it is reached through its pages, and a screen that shows one company says which.
 */
import type { Membership } from '../lib/api.ts';
import { formatOrgnr } from '@autho/i18n';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, Check, ChevronsUpDown, UserRound } from 'lucide-react';
import { useT } from '../lib/preferences.tsx';
import { useTenant } from '../lib/tenant.tsx';

export function ProfileSwitcher() {
  const t = useT();
  const { membership, memberships, selectTenant } = useTenant();

  const name = (m: Membership) => (m.tenantKind === 'me' ? t('tenant.kind.me') : m.tenantName);
  /** What kind of profile it is, in words: the person's own, or an organisation and its number. */
  const kind = (m: Membership) =>
    m.tenantKind === 'me'
      ? t('profile.kind.me')
      : m.orgnr === null
        ? t('tenant.kind.org')
        : t('profile.kind.orgNumbered', { orgnr: formatOrgnr(m.orgnr) });
  const glyph = (m: Membership) =>
    m.tenantKind === 'me' ? (
      <UserRound aria-hidden className="size-4 shrink-0 text-muted" />
    ) : (
      <Building2 aria-hidden className="size-4 shrink-0 text-muted" />
    );

  return (
    /**
     * **Not modal, and not portalled**: the list opens inside the banner, so it is in a landmark like everything a
     * person reads, and the rest of the page is not hidden from a screen reader while it is open — a modal menu
     * hid the page with focusable things still in it, which axe reports and a screen reader stumbles on.
     */
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger
        data-testid="profile-switcher"
        className="flex w-full min-w-0 items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-hover focus-visible:outline-2 focus-visible:outline-accent"
      >
        {glyph(membership)}
        {/* The control's purpose, for a screen reader, ahead of the words a sighted person sees on it. */}
        <span className="sr-only">{t('profile.switch')}: </span>
        {/* Two lines, as each row of its list is: it sits in the sidebar's column (F356), where one line cut the
            profile's name short behind its kind and number. */}
        {/* On a phone the menu is a rail of icons, and the switcher is its first: the glyph says Personal or a
            company, and the name stays for a screen reader. */}
        <span className="sr-only min-w-0 flex-1 flex-col md:not-sr-only md:flex">
          <span className="truncate font-medium">{name(membership)}</span>
          <span className="truncate text-xs text-muted">{kind(membership)}</span>
        </span>
        <ChevronsUpDown aria-hidden className="hidden size-3.5 shrink-0 text-muted md:block" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        sideOffset={4}
        className="z-50 w-80 rounded-md border border-border bg-surface p-1 shadow-lg"
      >
        <DropdownMenu.Label className="px-2 py-1 text-xs font-medium text-muted">
          {t('profile.switch')}
        </DropdownMenu.Label>
        <DropdownMenu.RadioGroup
          value={membership.tenantId}
          onValueChange={(id) => {
            if (id !== membership.tenantId) selectTenant(id);
          }}
        >
          {memberships.map((m) => (
            <DropdownMenu.RadioItem
              key={m.tenantId}
              value={m.tenantId}
              data-testid="profile-option"
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-hover"
            >
              {glyph(m)}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">{name(m)}</span>
                <span className="truncate text-xs text-muted">{kind(m)}</span>
              </span>
              <DropdownMenu.ItemIndicator>
                <Check aria-hidden className="size-3.5" />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
